import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, X, ArrowRight } from "lucide-react";
import { useReserve } from "@/contexts/ReserveContext";

export default function ObsessionList() {
  const { obsession, removeFromObsession, moveToReserveFromObsession } = useReserve();

  const onMove = (item) => {
    moveToReserveFromObsession(item.id);
    toast.success("Moved to The Reserve");
  };
  const onRemove = (item) => {
    removeFromObsession(item.id);
    toast("Removed from Obsession List");
  };

  return (
    <div data-testid="obsession-page" className="pt-28 md:pt-36 pb-24 md:pb-32">
      <section className="mx-auto max-w-[1100px] px-6 md:px-10">
        <p className="label-eyebrow text-lavender">Obsession List</p>
        <h1 className="font-display text-bone text-5xl md:text-7xl mt-5 leading-[0.95]">
          Obsession <em className="not-italic text-lavender">List.</em>
        </h1>
        <p className="font-display italic text-bone/75 text-xl md:text-2xl mt-5 max-w-xl">
          Things worth coming back for.
        </p>
        <p className="mt-4 text-bone/60 max-w-xl">
          A collection of discoveries, curiosities, and future indulgences.
        </p>

        {obsession.length === 0 ? (
          <EmptyObsession />
        ) : (
          <ul data-testid="obsession-list" className="mt-14 divide-y divide-bone/10 border-y border-bone/10">
            {obsession.map((item, idx) => (
              <li
                key={item.id}
                data-testid={`obsession-item-${item.id}`}
                className="py-7 grid grid-cols-[auto_1fr_auto] md:grid-cols-[auto_1fr_auto_auto] items-baseline gap-x-6 gap-y-3"
              >
                <Heart size={14} className="text-lavender mt-1" />
                <div className="col-span-2 md:col-span-1">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="font-display text-bone text-2xl md:text-3xl">{item.name}</span>
                    <span className="text-bone/35 text-[10px] uppercase tracking-[0.28em]">
                      {item.chapterTitle} · N° {String(idx + 1).padStart(2, "0")}
                    </span>
                  </div>
                  {item.desc && (
                    <p className="mt-2 text-bone/60 text-sm md:text-base leading-relaxed max-w-xl">{item.desc}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid={`obsession-move-${item.id}`}
                    onClick={() => onMove(item)}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-brass/60 text-brass text-[10px] uppercase tracking-[0.28em] hover:bg-brass hover:text-obsidian transition-colors"
                  >
                    Move to The Reserve <ArrowRight size={12} />
                  </button>
                  <button
                    type="button"
                    data-testid={`obsession-remove-${item.id}`}
                    onClick={() => onRemove(item)}
                    className="text-bone/45 hover:text-bone p-2 -m-2 transition-colors"
                    aria-label="Remove"
                    title="Remove from Obsession List"
                  >
                    <X size={16} />
                  </button>
                </div>
                <span className="font-display text-brass text-xl md:text-2xl hidden md:inline">${item.price}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function EmptyObsession() {
  return (
    <div data-testid="obsession-empty" className="mt-16 border border-bone/10 px-8 py-16 md:py-24 text-center">
      <p className="label-eyebrow text-lavender">Discoveries</p>
      <h2 className="font-display text-bone text-3xl md:text-5xl mt-5 leading-tight">
        Your Obsession List is empty.
      </h2>
      <p className="mt-6 font-display italic text-bone/70 text-lg md:text-xl max-w-xl mx-auto">
        You haven&apos;t found your next obsession yet.
      </p>
      <p className="mt-3 text-bone/60 max-w-md mx-auto">Go explore The House.</p>
      <div className="mt-10 flex flex-wrap justify-center gap-3">
        <Link
          to="/menu"
          data-testid="obsession-empty-cta-menu"
          className="inline-flex items-center px-6 py-3 bg-lavender text-obsidian text-[11px] uppercase tracking-[0.3em] hover:bg-bone transition-colors"
        >
          Open The Menu
        </Link>
        <Link
          to="/drops"
          data-testid="obsession-empty-cta-drops"
          className="inline-flex items-center px-6 py-3 border border-bone/30 text-bone text-[11px] uppercase tracking-[0.3em] hover:border-brass hover:text-brass transition-colors"
        >
          See The Limited Drops
        </Link>
      </div>
    </div>
  );
}
